import * as React from 'react'
import { ReactElement, useState } from 'react'
import { CreateDialogProps, EntityType } from './EntityField'
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Input,
    InputOnChangeData,
    Label,
} from '@fluentui/react-components'
import { AddCircleRegular, CheckmarkRegular, DismissRegular } from '@fluentui/react-icons'
import { Stack, StackItem } from '@fluentui/react'
import { Formik, useFormikContext } from 'formik'
import Form from '../Form'
import * as Yup from 'yup'
import useViewport, { Viewport } from '../../../hooks/useViewport'
import useAxios from '../../../hooks/useAxios'
import useHost from '../../../hooks/useHost'
import { UnitDto } from '../../../context/GlobalContext'
import { normalizeAxiosError, NormalizedError } from '../../../reducers/errorReducer'
import { AccountingId } from '../../../config/finance/invoiceConfig'
import { AxiosInstance, AxiosResponse } from 'axios'
import { useTranslation } from 'react-i18next'

type Props = CreateDialogProps & {
    externalType: EntityType
    unit: UnitDto
    initialValues: AddDimensionValues
}

export type AddDimensionValues = {
    autoGenerate?: boolean
    code?: string
    name: string
}

const validationSchema = Yup.object().shape({
    code: Yup.string().when('autoGenerate', {
        is: false,
        then: schema => schema.required('Voer een nummer in'),
    }),
    name: Yup.string().required('Voer een naam in'),
})

function Content() {
    const { t: translate } = useTranslation()

    const { values, setFieldValue } = useFormikContext<AddDimensionValues>()
    const { isViewport } = useViewport()

    return (
        <Stack tokens={{ childrenGap: 's1' }}>
            <Stack>
                <StackItem>
                    <Label htmlFor="add-accounting-dimension-code">{translate('accounting.dimensions.code')}</Label>
                </StackItem>
                <Stack
                    horizontal={isViewport('>=', Viewport.Large)}
                    tokens={{ childrenGap: isViewport('>=', Viewport.Large) ? 'm' : 's2' }}
                >
                    <Input
                        id="add-accounting-dimension-code"
                        style={{ flexGrow: 1 }}
                        value={(!values.autoGenerate && values.code) || ''}
                        disabled={values.autoGenerate}
                        onChange={(_, { value }: InputOnChangeData) => setFieldValue('code', value)}
                    />
                    <Checkbox
                        checked={values.autoGenerate || false}
                        style={{ minWidth: 0 }}
                        label={{
                            style: {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            },
                            children: translate('accounting.dimensions.autoGenerate'),
                        }}
                        onClick={() => setFieldValue('autoGenerate', !values.autoGenerate)}
                    />
                </Stack>
            </Stack>
            <Stack>
                <Label htmlFor="add-accounting-dimension-name">{translate('accounting.dimensions.name')}</Label>
                <Input
                    id="add-accounting-dimension-name"
                    value={values.name || ''}
                    onChange={(_, { value }: InputOnChangeData) => setFieldValue('name', value)}
                />
            </Stack>
        </Stack>
    )
}

function Body(props: Props & { globalError?: NormalizedError }): ReactElement {
    const { submitForm, isSubmitting } = useFormikContext<AddDimensionValues>()
    const { t: translate } = useTranslation()

    return (
        <DialogBody>
            <DialogTitle>
                {translate(
                    `accounting.dimensions.create.${props.externalType.toString().toLowerCase().replaceAll(/[-_]/g, '')}`,
                )}
            </DialogTitle>
            <DialogContent style={{ boxSizing: 'content-box' }}>
                <Form globalErrors={props.globalError ? [props.globalError] : undefined}>
                    <Content />
                </Form>
            </DialogContent>
            <DialogActions>
                <DialogTrigger disableButtonEnhancement>
                    <Button appearance="transparent" icon={<DismissRegular />}>
                        {translate('action.cancel')}
                    </Button>
                </DialogTrigger>
                <Button appearance="primary" icon={<CheckmarkRegular />} onClick={submitForm} disabled={isSubmitting}>
                    {translate('action.save')}
                </Button>
            </DialogActions>
        </DialogBody>
    )
}

export function createDimension(
    axios: AxiosInstance,
    externalType: string,
    hostKey: string,
    unitKey: string,
    data: Pick<AccountingId, 'code' | 'name'>,
): Promise<AxiosResponse<AccountingId>> {
    return axios.post(`/accounting/create/${externalType}/${hostKey}/${unitKey}`, data, {
        baseURL: process.env.API_HOST,
        headers: { SuppressErrors: 'true' },
    })
}

export default function CreateDimensionDialog(props: Props): ReactElement {
    const axios = useAxios()
    const { RowKey: hostKey } = useHost()
    const [open, setOpen] = useState(false)
    const [globalError, setGlobalError] = useState<NormalizedError>()

    return (
        <Dialog open={open} onOpenChange={(_, data) => setOpen(data.open)}>
            <DialogTrigger>
                <Button size="small" icon={<AddCircleRegular />} title="" appearance="transparent" />
            </DialogTrigger>
            <DialogSurface>
                <Formik<AddDimensionValues>
                    initialValues={props.initialValues}
                    validateOnChange={false}
                    validateOnBlur={false}
                    validationSchema={validationSchema}
                    onSubmit={(values: AddDimensionValues) => {
                        const data = {
                            code: values.autoGenerate ? undefined : values.code,
                            name: values.name,
                        }
                        return createDimension(axios, props.externalType, hostKey, props.unit.key, data)
                            .then(response => {
                                props.onCreate(response.data)
                                setOpen(false)
                            })
                            .catch(error => setGlobalError(normalizeAxiosError(error)))
                    }}
                >
                    <Body {...props} globalError={globalError} />
                </Formik>
            </DialogSurface>
        </Dialog>
    )
}
